import React, { useEffect, useRef, useState } from 'react'
import Icon from '../Icon'
import { playMilestone, unlockAudio } from './journeySound'

const STEPS = [
    { icon: 'house', title: 'Add Farm', text: 'Register your farm and manager details.' },
    { icon: 'egg', title: 'Add Flock', text: 'Log breed, hatch date and flock size.' },
    { icon: 'clipboard', title: 'Track Growth', text: 'Record feed, weight and mortality weekly.' },
    { icon: 'stethoscope', title: 'Vet Support', text: 'Book visits and get expert guidance.' },
    { icon: 'cart', title: 'Sell / Trade', text: 'List products or buy from the marketplace.' },
    { icon: 'chart', title: 'View Reports', text: 'Review performance and plan the next cycle.' },
]

const DRAW_MS = 3200    // time for the line to reach the last step
const PAUSE_MS = 220    // beat on each milestone before moving on

// The line draws itself across the row and lights each milestone as it reaches
// it. Runs once, when the section scrolls into view; the returned ref holds a
// replay function. onLightRef.current(i, n) is called as each milestone lights.
const useJourney = (wrapRef, litRef, onLightRef) => {
    const replayRef = useRef(null)

    useEffect(() => {
        const wrap = wrapRef.current
        const lit = litRef.current
        const steps = Array.from(wrap.querySelectorAll('.journey-step'))
        const n = steps.length
        let raf = 0
        const timeouts = []

        const light = (i) => {
            steps[i].classList.add('lit')
            onLightRef.current?.(i, n)
        }

        const run = () => {
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            const mobile = window.matchMedia('(max-width: 900px)').matches
            if (reduced || mobile) {
                steps.forEach((st, i) => timeouts.push(setTimeout(() => light(i), 140 * i)))
                return
            }

            const seg = DRAW_MS / (n - 1)
            const total = DRAW_MS + PAUSE_MS * (n - 1)
            let litCount = 0
            const t0 = performance.now()

            const frame = (now) => {
                const elapsed = now - t0
                let prog = 0
                let remaining = elapsed
                for (let i = 0; i < n - 1; i++) {
                    if (remaining <= seg) { prog = (i + remaining / seg) / (n - 1); break }
                    remaining -= seg
                    if (remaining <= PAUSE_MS) { prog = (i + 1) / (n - 1); break }
                    remaining -= PAUSE_MS
                    prog = (i + 1) / (n - 1)
                }
                if (elapsed >= total) prog = 1

                lit.style.strokeDashoffset = 1000 - 1000 * prog

                const reached = Math.min(n - 1, Math.floor(prog * (n - 1) + 0.001))
                while (litCount <= reached) light(litCount++)

                if (elapsed < total) raf = requestAnimationFrame(frame)
            }
            raf = requestAnimationFrame(frame)
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                observer.disconnect()
                run()
            }
        }, { threshold: 0.35 })
        observer.observe(wrap)

        const stop = () => {
            cancelAnimationFrame(raf)
            timeouts.forEach(clearTimeout)
            timeouts.length = 0
        }

        replayRef.current = () => {
            observer.disconnect()
            stop()
            steps.forEach((st) => st.classList.remove('lit'))
            lit.style.strokeDashoffset = 1000
            void wrap.offsetWidth // commit the reset so the ripples play again
            run()
        }

        return () => {
            observer.disconnect()
            stop()
        }
    }, [wrapRef, litRef, onLightRef])

    return replayRef
}

const Journey = () => {
    const wrapRef = useRef(null)
    const litRef = useRef(null)
    const onLightRef = useRef(null)
    const replayRef = useJourney(wrapRef, litRef, onLightRef)
    const [sound, setSound] = useState(false)

    // Sound is off by default. Turning it on replays the journey so the visitor
    // hears it straight away; the click is what lets the browser play audio.
    // onLightRef is only ever set here, never during render.
    const toggleSound = () => {
        if (sound) {
            onLightRef.current = null
            setSound(false)
            return
        }
        unlockAudio().then((ok) => {
            if (!ok) return
            onLightRef.current = playMilestone
            setSound(true)
            replayRef.current?.()
        })
    }

    return (
        <section className="section section-cream" id="journey">
            <div className="egg-bg a" /><div className="egg-bg b" />
            <div className="wrap">
                <div className="head-block reveal">
                    <span className="eyebrow">Your Farm Journey</span>
                    <h2>From setup to sale — every step, connected</h2>
                    <p className="sub">Poultryfy follows your flock through its whole lifecycle, so nothing gets lost between a notebook and a phone call.</p>
                    <button type="button" className="sound-toggle" onClick={toggleSound} aria-pressed={sound}>
                        <Icon name={sound ? 'volume' : 'volumeOff'} />
                        {sound ? 'Sound on' : 'Play with sound'}
                    </button>
                </div>
                <div className="journey-wrap" ref={wrapRef}>
                    <svg className="journey-svg" viewBox="0 0 1000 6" preserveAspectRatio="none" aria-hidden="true">
                        <path className="journey-path" d="M 0 3 L 1000 3" pathLength="1000" />
                        <path className="journey-path-lit" ref={litRef} d="M 0 3 L 1000 3" pathLength="1000" />
                    </svg>
                    <div className="journey-track">
                        {STEPS.map((s) => (
                            <div className="journey-step" key={s.title}>
                                <div className="journey-num"><Icon name={s.icon} /></div>
                                <h4>{s.title}</h4>
                                <p>{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Journey
